import { logDebug } from '@/utils/secureLogger';

/**
 * Student status types
 */
export type StudentStatus = 'pending' | 'active' | 'Active' | 'verified' | 'rejected' | 'document_issue' | 'approved' | 'resubmitted_under_review';

/**
 * Check if a student can access features based on their status
 * @param status - The student's current status
 * @returns boolean indicating if the student can access features
 */
export function canStudentAccessFeatures(status: StudentStatus): boolean {
  logDebug('Checking student access features', { status });
  
  // Students with approved, active, or verified status can access features
  // Check for both old status field and new applicationStatus field
  return status === 'approved' || status === 'active' || status === 'Active' || status === 'verified';
}

/**
 * Get a human-readable status message for the student
 * @param status - The student's current status
 * @returns string message describing the status
 */
export function getStatusMessage(status: StudentStatus): string {
  logDebug('Getting status message', { status });
  
  switch (status) {
    case 'pending':
      return 'Your account is pending approval. You will be able to book sessions and send messages once approved.';
    case 'approved':
    case 'active':
    case 'verified':
      return 'Your account is active. You can book sessions and send messages.';
    case 'rejected':
      return 'Your account application was not approved. Please contact support for more information.';
    case 'document_issue':
      return 'There are issues with your submitted documents. Please resubmit the required documents.';
    case 'resubmitted_under_review':
      return 'Your resubmitted documents are being reviewed. You will be notified once the review is complete.';
    default:
      return 'Unknown account status. Please contact support.';
  }
}

/**
 * Get the display name for a student status
 * @param status - The student's current status
 * @returns string display name for the status
 */
export function getStatusDisplayName(status: StudentStatus): string {
  switch (status) {
    case 'pending':
      return 'Pending Approval';
    case 'approved':
    case 'active':
    case 'verified':
      return 'Active';
    case 'rejected':
      return 'Rejected';
    case 'document_issue':
      return 'Document Issues';
    case 'resubmitted_under_review':
      return 'Resubmitted - Under Review';
    default:
      return 'Unknown';
  }
}

/**
 * Check if a student status allows booking sessions
 * @param status - The student's current status
 * @returns boolean indicating if the student can book sessions
 */
export function canBookSessions(status: StudentStatus): boolean {
  return canStudentAccessFeatures(status);
}

/**
 * Check if a student status allows messaging
 * @param status - The student's current status
 * @returns boolean indicating if the student can send messages
 */
export function canMessage(status: StudentStatus): boolean {
  return canStudentAccessFeatures(status);
}
